/**
 * Villagers.
 *
 * The first thing in the world that is not a block. He stands where he was
 * put, turns to face whoever pokes him, and says one thing - which is the
 * whole point of him: he is the game's first joke, and the first content that
 * exists only when the 18+ switch is on.
 *
 * Kept deliberately small. There is no pathfinding, no schedule and no AI
 * here, because none of that is needed to tell whether the idea is any fun.
 * What is here is the part the rest will need either way: a position, a
 * facing, a hit test against the aiming ray, and a line with a clock on it.
 */

import { PLAYER_HEIGHT, PLAYER_WIDTH } from "../player/controller.js";
import type { LineKey } from "../text/tone.js";
import {
  boxOverlapsVoxel, moveAABB, type SolidTest,
} from "../physics/voxelCollision.js";
import { pickEntity } from "./aim.js";
import { Vitals } from "./vitals.js";

export const VILLAGER_WIDTH = PLAYER_WIDTH;
export const VILLAGER_HEIGHT = PLAYER_HEIGHT;

/** How long a line stays up after he is hit, in seconds. */
export const SPEECH_SECONDS = 3.5;

export interface Villager {
  readonly id: number;
  /** Shown in front of whatever he says. */
  readonly name: string;
  readonly skinId: string;
  /** What he or she says when hit. Looked up in both tones, like everything. */
  readonly line: LineKey;
  /** Feet position, like the player's. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Seconds of his current line still to run. 0 when he is quiet. */
  speaking: number;
  /**
   * How many times he has spoken.
   *
   * Counted up rather than read off `speaking`, for the reason a blow is
   * counted rather than timed: over a network the seconds arrive fifteen
   * times a second and a line that is still running looks the same as one
   * just started. A number that changes says *he has just said something*,
   * once, and the voice plays there.
   */
  said: number;
  /** What it takes to put him down. */
  readonly vitals: Vitals;

  /* ---- and a life, for the ones who live in a village ------------------ */

  readonly kind: VillagerKind;
  /**
   * The door they came out of, and what the wander leash is measured from.
   *
   * For the two who stand near the player's own spawn this is simply where
   * they were put, which makes them wander round that spot - they were
   * statues before, and a statue was never the idea.
   */
  readonly home: { x: number; y: number; z: number };
  /** The cell their bed stands in, or null for somebody who has no bed. */
  readonly bed: { x: number; y: number; z: number } | null;
  /** The plot they work by day, or null for a child and for the two strays. */
  readonly plot: { x: number; y: number; z: number } | null;
  /**
   * The doorway of the house they live in, and the room behind it.
   *
   * "Вони мають розуміти де вихід з будинку." Without these a villager
   * indoors wandered on a random heading and jumped at whichever wall it
   * met - a room is four walls and one cell of door, and a random walk finds
   * the walls. With them, anybody whose business is on the other side of a
   * wall goes to the door first and through it.
   */
  readonly door: { x: number; y: number; z: number } | null;
  /** The cell outside that door, which is where "out" actually is. */
  readonly porch: { x: number; y: number; z: number } | null;
  /** And the cell inside it, which is where "in" is. */
  readonly mat: { x: number; y: number; z: number } | null;
  readonly room: { x0: number; x1: number; z0: number; z1: number } | null;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  /** Seconds left of this leg of the wander, and where it was going. */
  leg: number;
  legYaw: number;
  legMoving: boolean;
  /** In bed. Nothing moves and the renderer lays the body down. */
  asleep: boolean;
  /** Seconds of work put in over the plot they are standing on. */
  tend: number;
  /**
   * Seconds left of following a wall, and which way along it.
   *
   * A villager walks at what they want, and what they want is often behind
   * a хата: the door faces the square and the город is out the other side.
   * Without this they walk into the back wall of their own house and stay
   * there. It is not pathfinding - it is the one rule that gets you round a
   * box, and a village is boxes.
   */
  slide: number;
  slideX: number;
  slideZ: number;
  /** Distance walked, for the stride. */
  walked: number;
}

/**
 * How much a villager can take.
 *
 * Less than a player, and deliberately: he is a man in a shirt, not a soldier,
 * and the orcs coming for him are the point of the game rather than he is.
 */
export const VILLAGER_MAX_HP = 12;

let nextId = 1;

/** Everything about a villager that is not where they are standing. */
export interface Life {
  readonly kind: VillagerKind;
  readonly home?: { x: number; y: number; z: number };
  readonly bed?: { x: number; y: number; z: number };
  readonly plot?: { x: number; y: number; z: number };
  readonly door?: { x: number; y: number; z: number };
  readonly porch?: { x: number; y: number; z: number };
  readonly mat?: { x: number; y: number; z: number };
  readonly room?: { x0: number; x1: number; z0: number; z1: number };
}

export function makeVillager(
  name: string, skinId: string, line: LineKey,
  at: { x: number; y: number; z: number },
  life: Life = { kind: "чоловік" },
): Villager {
  return {
    id: nextId++, name, skinId, line,
    x: at.x, y: at.y, z: at.z, yaw: 0, speaking: 0, said: 0,
    vitals: new Vitals(VILLAGER_MAX_HP),
    kind: life.kind,
    home: life.home ?? { x: at.x, y: at.y, z: at.z },
    bed: life.bed ?? null,
    plot: life.plot ?? null,
    door: life.door ?? null,
    porch: life.porch ?? null,
    mat: life.mat ?? null,
    room: life.room ?? null,
    vx: 0, vy: 0, vz: 0, onGround: false,
    leg: 0, legYaw: 0, legMoving: false,
    asleep: false, tend: 0, walked: 0, slide: 0, slideX: 0, slideZ: 0,
  };
}


/**
 * One the server owns, drawn here.
 *
 * Built rather than made-and-renumbered, for the reason `mirrorOrc` is: the
 * makers take the next local id as they go, so mirroring a herd would push
 * this world's own numbering along for bodies it never made.
 */
export function mirrorVillager(
  id: number, name: string, skinId: string, line: LineKey,
  at: { x: number; y: number; z: number },
): Villager {
  return {
    id, name, skinId, line,
    x: at.x, y: at.y, z: at.z, yaw: 0, speaking: 0, said: 0,
    vitals: new Vitals(VILLAGER_MAX_HP),
    // Nothing of their own: everything about a mirrored body arrives on the
    // wire, and a schedule of its own would have it walking off to a bed the
    // server has never heard of.
    kind: "чоловік",
    home: { x: at.x, y: at.y, z: at.z },
    bed: null, plot: null, door: null, porch: null, mat: null, room: null,
    vx: 0, vy: 0, vz: 0, onGround: false,
    leg: 0, legYaw: 0, legMoving: false,
    asleep: false, tend: 0, walked: 0, slide: 0, slideX: 0, slideZ: 0,
  };
}

/**
 * Run down the speech timers, and nothing else.
 *
 * What mirrored villagers get: their bodies are the server's and everything
 * about them arrives on the wire, so stepping them here would be two
 * simulations disagreeing about where somebody is standing.
 */
export function tickVillagers(list: Villager[], dt: number): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const v = list[i]!;
    if (v.speaking > 0) v.speaking = Math.max(0, v.speaking - dt);
    v.vitals.step(dt);
    // Nothing is left behind. There is no body, no death animation and no
    // bundle of his things - all of that is work for when there is something
    // in this world worth killing.
    if (v.vitals.dead) list.splice(i, 1);
  }
}

/** Turn him to look at a point, so being hit spins him round to face you. */
export function faceToward(v: Villager, x: number, z: number): void {
  // Yaw 0 faces -Z, matching the player and the skin layout, so the forward
  // vector is (-sin yaw, -cos yaw) and the yaw that points at a delta is the
  // arctangent of its negation. Adding pi to atan2(dx, -dz) looks equivalent
  // and is exactly backwards - he turned his back on whoever hit him.
  v.yaw = Math.atan2(-(x - v.x), -(z - v.z));
}

/**
 * Whoever is standing in this block cell, if anybody is.
 *
 * The world is voxels and the people in it are not, so nothing in the block
 * code can see them - which is how a player ends up bricking дядько Гриць into
 * a wall up to his chest. Every placement asks this first.
 *
 * Reuses the player's own box test rather than writing a second one: a
 * villager occupies space exactly the way a player does, and two overlap
 * tests that are meant to agree eventually stop agreeing.
 */
export function villagerInCell(
  list: readonly Villager[], x: number, y: number, z: number,
): Villager | null {
  for (const v of list) {
    if (boxOverlapsVoxel(
      v.x, v.y, v.z, VILLAGER_WIDTH / 2, VILLAGER_HEIGHT, x, y, z,
    )) return v;
  }
  return null;
}

/**
 * The nearest villager the ray passes through, within `maxDistance`.
 *
 * A slab test against an upright box round his feet. Blocks are found by
 * marching the voxel grid, which cannot see entities at all, so this runs
 * alongside that and whichever hit is closer wins.
 */
export function hitVillager(
  list: readonly Villager[],
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDistance: number,
): { villager: Villager; distance: number } | null {
  const found = pickEntity(
    list, ox, oy, oz, dx, dy, dz, maxDistance, VILLAGER_WIDTH, VILLAGER_HEIGHT,
  );
  return found === null ? null : { villager: found.entity, distance: found.distance };
}

/* ---- who they are ------------------------------------------------------- */

/**
 * Village names, by who is wearing them.
 *
 * Дядько and тітка in front of the grown-ups, the way the first two in this
 * game were named and the way a child in a village addresses anybody older
 * than they are. The children go by the diminutive, which is the only name
 * anybody would use for them.
 *
 * Picked by a hash of where the house stands, so the same хата holds the
 * same family every time it is walked into.
 */
const NAMES: Readonly<Record<VillagerKind, readonly string[]>> = {
  "чоловік": [
    "дядько Гриць", "дядько Остап", "дядько Панас", "дядько Тарас",
    "дядько Микита", "дядько Степан", "дядько Опанас", "дядько Яків",
  ],
  "жінка": [
    "тітка Одарка", "тітка Мотря", "тітка Горпина", "тітка Оксана",
    "тітка Килина", "тітка Параска", "тітка Ганна", "тітка Явдоха",
  ],
  "хлопець": [
    "Івасик", "Петрусь", "Павлик", "Миколка", "Юрко", "Сашко",
  ],
  "дівчина": [
    "Галя", "Марійка", "Оленка", "Христя", "Наталка", "Ївга",
  ],
};

/** The skin each of the four wears. */
const SKIN_OF: Readonly<Record<VillagerKind, string>> = {
  "чоловік": "selianyn",
  "жінка": "selianka",
  "хлопець": "khlopchyk",
  "дівчина": "divchynka",
};

/** And what each of them says when somebody takes a swing at them. */
const LINE_OF: Readonly<Record<VillagerKind, LineKey>> = {
  "чоловік": "villager-hit",
  "жінка": "villager-hit-her",
  "хлопець": "villager-hit-boy",
  "дівчина": "villager-hit-girl",
};

export function skinForKind(kind: VillagerKind): string {
  return SKIN_OF[kind];
}

export function lineForKind(kind: VillagerKind): LineKey {
  return LINE_OF[kind];
}

/**
 * A name for somebody of this kind, chosen by a number.
 *
 * `roll` is anything in 0..1 - a hash of where they live, in practice - so
 * the choice is a fact about the village rather than about when you happened
 * to walk into it.
 */
export function nameForKind(kind: VillagerKind, roll: number): string {
  const names = NAMES[kind];
  return names[Math.min(names.length - 1, Math.floor(roll * names.length))]!;
}

/** Every name the village can hand out, for a test to look at. */
export const VILLAGER_NAMES = NAMES;

/* ---- a life ------------------------------------------------------------- */

/**
 * Who somebody is in a village.
 *
 * "Там де 2 ліжка - жінку й чоловіка, а де одне ліжко - рандомно чи жінку чи
 * чоловіка. Створи дітей і дівчат і хлопців." Four kinds, and the difference
 * between them is not cosmetic: the grown-ups have a bed and a plot to work
 * and the children have neither - they sleep on the floor of the house they
 * were born in and they play.
 */
export type VillagerKind = "чоловік" | "жінка" | "хлопець" | "дівчина";

/**
 * How far an idle villager strays from home, in blocks.
 *
 * "Нехай селяни навчаться ходити але далі ніж 10 блоків від села не
 * відходять або від місця їх спавна." Ten, from the door they came out of.
 *
 * It bounds *wandering* and not the day's walk: going out to the plot and
 * back is where a villager is meant to be, not straying, and a rule that
 * kept everybody within ten blocks of the house would leave the городи to
 * grow themselves.
 */
export const WANDER_LEASH = 10;

/** Blocks a second. Slower than a walk; a child is quicker and never still. */
export const VILLAGER_SPEED = 2.4;
export const CHILD_SPEED = 3.1;

/** How close to the plot counts as working it. */
export const WORK_REACH = 1.8;

/** Seconds of work a crop takes to come on one stage. */
export const TEND_SECONDS = 5;

/** How close to the bed counts as being in it. */
export const BED_REACH = 1.1;

const WANDER_SECONDS = 5;
/** How much of the time a wandering villager is simply standing about. */
const IDLE_SHARE = 0.45;
const GRAVITY = 32;
const TERMINAL = 48;
const DAMPING = 8;
/**
 * How hard they push off, in blocks a second.
 *
 * `v = sqrt(2*g*h)`, and the height has to be worth more than the one block
 * being cleared: the impulse is set and then a whole tick of gravity is taken
 * off it before anybody moves, and a villager's tick is a wall-clock frame
 * rather than the world's fixed sixtieth. At 8.4 that arithmetic came to 0.97
 * of a block at sixty frames and less below it - "діти не можуть застрибнути
 * на 1 блок висоти, то ж застрягають в ямах", and it was not only the
 * children. 9.6 clears 1.44, which leaves room for a slow frame.
 */
const JUMP = 9.6;
const STEP_UP = 0.6;
/** How far a villager will drop off a ledge rather than turn back. */
const MAX_DROP = 3;
/** How long a villager keeps a wall beside them before trying straight on. */
const SLIDE_SECONDS = 1.5;

/**
 * Somewhere to put a foot.
 *
 * The same question the beasts ask, asked the same way: water is not a floor,
 * a block to step onto is not a drop, and a hole deeper than three blocks is
 * a hole a person does not walk into. A villager who could not answer it
 * walked into the річка and stood there.
 */
function footing(
  isSolid: SolidTest, isFluid: SolidTest,
  x: number, y: number, z: number, dirX: number, dirZ: number,
): boolean {
  const ax = Math.floor(x + dirX * (VILLAGER_WIDTH / 2 + 0.6));
  const az = Math.floor(z + dirZ * (VILLAGER_WIDTH / 2 + 0.6));
  const feet = Math.floor(y);
  if (isFluid(ax, feet, az) || isFluid(ax, feet - 1, az)) return false;
  if (isSolid(ax, feet, az)) return true;
  for (let d = 1; d <= MAX_DROP + 1; d++) {
    if (isSolid(ax, feet - d, az)) return d <= MAX_DROP;
  }
  return false;
}

/** What a step of village life produced, for the world to act on. */
export interface VillageTurn {
  /**
   * Whoever put in a shift over a plot this step.
   *
   * The world decides what that means - it is the one that knows there is a
   * crop there at all. "Вдень працюють, вирощують пшеницю": this is the
   * "працюють" half, and the growing is the caller's.
   */
  readonly tended: Villager[];
}

/**
 * A day in the village.
 *
 * Night: everybody goes to their bed, and the children lie down on the floor
 * of the house they were born in. Day: the grown-ups walk out to the plot and
 * work it, and the children play in the lane.
 *
 * Pure, like everything else that steps: positions, timers and two solidity
 * tests. It never touches a world and it never grows anything itself.
 */
export function stepVillagers(
  list: Villager[],
  wallClock: number,
  isSolid: SolidTest,
  isFluid: SolidTest,
  night: boolean,
  random: () => number = Math.random,
): VillageTurn {
  const tended: Villager[] = [];
  /*
   * A slow frame is still one step, not a leap.
   *
   * These run on wall-clock seconds rather than on the world's fixed tick -
   * a village should not stop being a village because the frame rate dipped
   * - and a frame that took half a second would otherwise move everybody
   * half a second in one go, through walls and off ledges.
   */
  const dt = Math.min(wallClock, 1 / 20);

  for (let i = list.length - 1; i >= 0; i--) {
    const v = list[i]!;
    if (v.speaking > 0) v.speaking = Math.max(0, v.speaking - dt);
    v.vitals.step(dt);
    // Nothing is left behind. There is no body and no bundle of his things.
    if (v.vitals.dead) { list.splice(i, 1); continue; }

    const child = v.kind === "хлопець" || v.kind === "дівчина";
    const speed = child ? CHILD_SPEED : VILLAGER_SPEED;

    /*
     * Where they are trying to be.
     *
     * One point, and everything else follows from it: null means "nowhere in
     * particular", which is what wandering is.
     */
    let goal: { x: number; y: number; z: number } | null = null;
    if (night) goal = v.bed;
    else if (!child) goal = v.plot;

    /*
     * And the door, when what they want is on the other side of a wall.
     *
     * "Вони мають розуміти де вихід з будинку." A room is four walls and one
     * cell of doorway; walking at the thing you want gets you a wall, and
     * the hop below then has you jumping at it. So anybody whose business is
     * outside goes to the door first and through it, and anybody whose
     * business is inside does the same in reverse.
     *
     * The doorway itself counts as *outside* the room - see `roomBox` - so
     * reaching it hands the real goal back on the next step rather than
     * leaving somebody turning round in their own doorway.
     */
    const real = goal;
    /**
     * The house they must not walk back into, or null.
     *
     * Set for somebody who is out of the room with an errand that is also
     * out of it: the straight line from the doorway to the город goes
     * through the хата, and without this they walked back in, were sent out
     * again, and stood in their own doorway going in and out for ever. With
     * it the house is simply a box in the way, and `slide` walks them round
     * a box.
     */
    let keepOut: { x0: number; x1: number; z0: number; z1: number } | null =
      null;
    if (v.mat !== null && v.porch !== null && v.room !== null) {
      const meIn = inRoom(v.room, v.x, v.z);
      /*
       * Out is the porch, in is the mat.
       *
       * The two cells either side of the doorway, and both are needed. A
       * door is a thing you walk *through*: aiming at the doorway itself
       * leaves you standing in it, and aiming at the errand aims you at a
       * wall. Walking to the far side of it is what makes you plainly out,
       * or plainly in, and hands the errand back.
       */
      if (real === null && !night && meIn) {
        // A child with nothing to do, indoors, in daylight: out to play.
        goal = v.porch;
      } else if (real !== null &&
        inRoom(v.room, real.x + 0.5, real.z + 0.5) !== meIn) {
        goal = meIn ? v.porch : v.mat;
      } else if (real !== null && !meIn &&
        !inRoom(v.room, real.x + 0.5, real.z + 0.5)) {
        keepOut = v.room;
      }
      /*
       * And nothing lets go of the door early.
       *
       * The first version released it once they were within a step of the
       * doorway, which is a foot short of being *through* it - and a man a
       * foot short of his own front door then turned and walked at the wall
       * behind him, because that is where the plot was. Standing in the
       * doorway is already outside the room (see `roomBox`), so `meIn` hands
       * the real goal back on its own, one step later and on the right side
       * of the wall.
       */
    }

    const toGoal = goal === null
      ? Infinity
      : Math.hypot(goal.x + 0.5 - v.x, goal.z + 0.5 - v.z);

    /*
     * In bed: down and still until morning.
     *
     * Asked of the *bed* rather than of whatever they are walking to. On the
     * way home the thing they are walking to is the doorway, and a doorway is
     * a block from the bed in a хата five wide - so a villager coming home
     * fell asleep standing in his own front door.
     */
    v.asleep = night && v.bed !== null && goal === v.bed && toGoal <= BED_REACH;
    if (v.asleep) {
      v.vx = 0;
      v.vz = 0;
      v.tend = 0;
    }

    let wantX = 0;
    let wantZ = 0;

    if (v.asleep) {
      // Nothing. A sleeping body still falls, which is what the physics below
      // is for, and otherwise it is where it is.
    } else if (goal !== null && goal !== real) {
      // The doorway: walked *through* rather than arrived at, so there is no
      // "near enough" - the next step is taken from the other side of it.
      const dx = goal.x + 0.5 - v.x;
      const dz = goal.z + 0.5 - v.z;
      const flat = Math.hypot(dx, dz) || 1;
      wantX = dx / flat;
      wantZ = dz / flat;
      v.yaw = yawTo(wantX, wantZ);
      v.tend = 0;
    } else if (goal !== null && toGoal > (night ? BED_REACH : WORK_REACH)) {
      const dx = goal.x + 0.5 - v.x;
      const dz = goal.z + 0.5 - v.z;
      const flat = Math.hypot(dx, dz) || 1;
      wantX = dx / flat;
      wantZ = dz / flat;
      v.yaw = yawTo(wantX, wantZ);
      v.tend = 0;
    } else if (goal !== null && !night) {
      /*
       * At the plot: work it.
       *
       * Standing over the row rather than walking about on it - a villager
       * who wandered while working would tend one cell of somebody else's
       * garden every few seconds.
       */
      v.tend += dt;
      if (v.tend >= TEND_SECONDS) {
        v.tend = 0;
        tended.push(v);
      }
      if (v.leg <= 0) {
        v.leg = WANDER_SECONDS;
        v.yaw = random() * Math.PI * 2;
      }
      v.leg -= dt;
    } else {
      /*
       * Wandering, on a leash.
       *
       * A new heading every few seconds, some of them spent standing still -
       * a village where everybody is permanently in motion reads as a
       * machine. Past the leash there is only one heading worth having, and
       * it is home.
       */
      const out = Math.hypot(v.x - (v.home.x + 0.5), v.z - (v.home.z + 0.5));
      v.leg -= dt;
      if (v.leg <= 0) {
        v.leg = WANDER_SECONDS * (0.5 + random());
        v.legMoving = random() > IDLE_SHARE;
        v.legYaw = random() * Math.PI * 2;
      }
      if (out > WANDER_LEASH) {
        // Straight back, and no argument about it.
        const dx = v.home.x + 0.5 - v.x;
        const dz = v.home.z + 0.5 - v.z;
        const flat = Math.hypot(dx, dz) || 1;
        wantX = dx / flat;
        wantZ = dz / flat;
        v.legMoving = true;
        v.legYaw = yawTo(wantX, wantZ);
        v.yaw = v.legYaw;
      } else if (v.legMoving) {
        v.yaw = v.legYaw;
        wantX = -Math.sin(v.legYaw);
        wantZ = -Math.cos(v.legYaw);
      }
    }

    /*
     * A wall in the way of somewhere they are going: follow it round.
     *
     * The door of a хата faces the square and its город is out the other
     * side, so "walk at the plot" walks into the back of your own house -
     * which is where they stood, jumping. This is not pathfinding. It is the
     * one rule that gets a body round a box: when what is ahead cannot be
     * climbed, turn a quarter and keep the wall beside you for a second or
     * two, then try straight on again.
     *
     * Only when they are going somewhere. A wanderer who meets a wall simply
     * picks another heading, which is what wandering is.
     */
    if (goal !== null && (wantX !== 0 || wantZ !== 0)) {
      /** A wall, or the house they have just come out of. */
      const shut = (dirX: number, dirZ: number): boolean =>
        blocked(isSolid, v, dirX, dirZ) ||
        (keepOut !== null &&
          inRoom(keepOut, v.x + dirX * 0.9, v.z + dirZ * 0.9));
      if (v.slide > 0) {
        v.slide -= dt;
        if (!shut(v.slideX, v.slideZ)) {
          wantX = v.slideX;
          wantZ = v.slideZ;
          v.yaw = yawTo(wantX, wantZ);
        } else {
          v.slide = 0;
        }
      }
      if (v.slide <= 0 && shut(wantX, wantZ)) {
        /*
         * Which way round: the side that is open, and of two open sides the
         * one that ends up nearer what they want.
         */
        const sides: [number, number][] = [[-wantZ, wantX], [wantZ, -wantX]];
        let best: [number, number] | null = null;
        let bestAway = Infinity;
        for (const [sx, sz] of sides) {
          if (shut(sx, sz)) continue;
          const away = Math.hypot(
            goal.x + 0.5 - (v.x + sx * 2), goal.z + 0.5 - (v.z + sz * 2));
          if (away < bestAway) { bestAway = away; best = [sx, sz]; }
        }
        if (best !== null) {
          v.slideX = best[0];
          v.slideZ = best[1];
          v.slide = SLIDE_SECONDS;
          wantX = best[0];
          wantZ = best[1];
          v.yaw = yawTo(wantX, wantZ);
        }
      }
    }

    // Nowhere to put a foot that way: stop, and pick a new heading now rather
    // than in five seconds' time.
    if ((wantX !== 0 || wantZ !== 0) &&
      !footing(isSolid, isFluid, v.x, v.y, v.z, wantX, wantZ)) {
      wantX = 0;
      wantZ = 0;
      v.leg = 0;
    }

    if (wantX !== 0 || wantZ !== 0) {
      v.vx = wantX * speed;
      v.vz = wantZ * speed;
      /*
       * A doorstep, a lane kerb, the step into a город: hop it.
       *
       * Only what can actually be climbed. A wall is a solid block with
       * another solid block on top of it, and jumping at one of those for
       * ever is what "селяни стрибають на стіни" looks like: the hop fired
       * on anything solid ahead, so a villager who walked into his own wall
       * bounced off it until the heading changed.
       */
      if (v.onGround) {
        const aheadX = v.x + wantX * (VILLAGER_WIDTH / 2 + 0.35);
        const aheadZ = v.z + wantZ * (VILLAGER_WIDTH / 2 + 0.35);
        const step = Math.floor(v.y + 0.2);
        if (isSolid(Math.floor(aheadX), step, Math.floor(aheadZ)) &&
          !isSolid(Math.floor(aheadX), step + 1, Math.floor(aheadZ))) {
          v.vy = JUMP;
          v.onGround = false;
        }
      }
      v.walked += speed * dt;
    } else {
      v.vx -= v.vx * Math.min(1, DAMPING * dt);
      v.vz -= v.vz * Math.min(1, DAMPING * dt);
    }

    v.vy = Math.max(-TERMINAL, v.vy - GRAVITY * dt);
    const moved = moveAABB(
      v.x, v.y, v.z, VILLAGER_WIDTH / 2, VILLAGER_HEIGHT,
      v.vx * dt, v.vy * dt, v.vz * dt, isSolid, STEP_UP, v.onGround,
    );
    v.x = moved.x;
    v.y = moved.y;
    v.z = moved.z;
    v.onGround = moved.onGround;
    if (moved.onGround && v.vy < 0) v.vy = 0;
    if (moved.hitCeiling && v.vy > 0) v.vy = 0;
  }

  return { tended };
}

/**
 * Something ahead that cannot be climbed.
 *
 * Solid at the feet *and* solid above it: one block is a step and two is a
 * wall. The same question the hop asks, so a villager never turns aside from
 * something they were about to hop over.
 */
function blocked(
  isSolid: SolidTest, v: Villager, dirX: number, dirZ: number,
): boolean {
  const ax = Math.floor(v.x + dirX * (VILLAGER_WIDTH / 2 + 0.45));
  const az = Math.floor(v.z + dirZ * (VILLAGER_WIDTH / 2 + 0.45));
  const feet = Math.floor(v.y + 0.2);
  return isSolid(ax, feet, az) && isSolid(ax, feet + 1, az);
}

/** Is this point inside the room, as opposed to in a wall or outdoors? */
function inRoom(
  room: { x0: number; x1: number; z0: number; z1: number },
  x: number, z: number,
): boolean {
  return x >= room.x0 && x < room.x1 + 1 && z >= room.z0 && z < room.z1 + 1;
}

/** Yaw that points along a flat heading. Yaw 0 faces -Z, like the player. */
function yawTo(dx: number, dz: number): number {
  return Math.atan2(-dx, -dz);
}
